import {
  KEAGraph,
  KEALink,
  KEAPaper,
  KEAPaperBuilder,
  LinkLabelPosition,
  LinkType,
  MarkerPosition,
  MarkerType,
  PaperEventTypes,
  keaNamespace,
} from "@kea-mod/jointjs";
import { InteractionType, KEAGraphContext } from "context/KEAGraphContext";
import { Component, createRef } from "react";
import { AppendPalette, AppendTarget } from "./AppendPalette";
import { ContextMenuTarget, ElementContextMenu } from "./ElementContextMenu";
import { InlineLabelEditor, InlineRenameTarget } from "./InlineLabelEditor";

const paperOptions: joint.dia.Paper.Options = {
  width: "100%",
  height: "100%",
  gridSize: 5,
  cellViewNamespace: keaNamespace,
  drawGrid: {
    name: "doubleMesh",
    args: [
      {
        color: "grey",
        thickness: 0.5,
      },
      {
        color: "#131313",
        scaleFactor: 16,
        thickness: 2,
      },
    ],
  },
  defaultConnectionPoint: { name: "boundary" },
  background: {
    color: "#EDF2FA",
  },
  // Require edges to end on a node instead of being dropped into empty space.
  linkPinning: false,
  validateConnection: function () {
    return true;
  },
  defaultAnchor: {
    name: "modelCenter",
    args: {
      rotate: true,
      padding: 20,
    },
  },
  interactive: { labelMove: false },
  defaultLink: () => {
    const link = new KEALink();
    link.setMarker(MarkerPosition.SOURCE_MARKER, MarkerType.NONE);
    link.setMarker(MarkerPosition.TARGET_MARKER, MarkerType.NONE);
    link.setDasharray(LinkType.SOLID);
    return link as joint.dia.Link;
  },
};

interface State {
  renaming: InlineRenameTarget | null;
  contextMenu: ContextMenuTarget | null;
  append: AppendTarget | null;
}

type Props = typeof IFramePrimaryGraph.defaultProps & {
  width: number;
  height: number;
  grid?: any;
  gridSize?: number;
  gridBackground?: string;
};

export class IFramePrimaryGraph extends Component<Props, State> {
  static defaultProps = {
    pushPresent: () => {},
    addUserInteraction: (
      _interactionType: InteractionType,
      _interactionTime: number,
      _payload?: KEAGraph | joint.dia.Link | joint.dia.Element | joint.dia.Cell[],
    ) => {},
    setNode: (_node: any) => {},
  };

  static contextType = KEAGraphContext;
  context!: React.ContextType<typeof KEAGraphContext>;

  private canvasContainerRef: React.RefObject<HTMLDivElement>;

  constructor(props: Props) {
    super(props);
    this.canvasContainerRef = createRef();
    this.state = { renaming: null, contextMenu: null, append: null };
  }

  componentDidMount = () => {
    //this is a hacky, but works. Joint elements need to be in DOM to import new graphs. Otherwise, the scaling goes wrong. There is no way to guarantee, that the virtualDOM is indeed present in the actual DOM.
    //look at https://stackoverflow.com/questions/26556436/react-after-render-code
    setTimeout(() => {
      const paper = this.initializePaper();
      paper.on("keaelement:rename", (view: joint.dia.ElementView, initial?: string) => {
        this.setState({
          renaming: { element: view.model as joint.dia.Element, paper, initial },
          contextMenu: null,
        });
      });
      paper.on("keaelement:append", (view: joint.dia.ElementView) => {
        this.setState({ append: { element: view.model as joint.dia.Element, paper } });
      });
      paper.on("keapaper:closeappend", () => this.setState({ append: null }));
      paper.on("kealink:renamelabel", (view: joint.dia.LinkView, position: LinkLabelPosition) => {
        // On short edges the edge tools sit exactly where the input field appears.
        paper.removeTools();
        this.setState({ renaming: { linkView: view, position, paper }, contextMenu: null });
      });
      paper.on("keaelement:contextmenu", (view: joint.dia.ElementView, evt: joint.dia.Event) => {
        const point = paper.clientToLocalPoint(evt.clientX ?? 0, evt.clientY ?? 0);
        const at = paper.localToPaperPoint(point.x, point.y);
        this.setState({ contextMenu: { view, paper, x: at.x, y: at.y }, renaming: null });
      });
      paper.on("kealink:contextmenu", (view: joint.dia.LinkView, evt: joint.dia.Event) => {
        const point = paper.clientToLocalPoint(evt.clientX ?? 0, evt.clientY ?? 0);
        const at = paper.localToPaperPoint(point.x, point.y);
        this.setState({ contextMenu: { view, paper, x: at.x, y: at.y, isLink: true }, renaming: null });
      });
      paper.on("keapaper:closecontextmenu", () => this.setState({ contextMenu: null }));
      this.context.setModelingPaper(paper);
    }, 1000);
  };

  componentWillUnmount = () => {
    this.context.modelingPaper.removeDragPaperEvent();
    this.context.modelingPaper.removeCopyPasteMessageEvents();
    this.context.modelingPaper.removeDeleteEvent();
  };

  initializePaper = (): KEAPaper => {
    return new KEAPaperBuilder(
      this.canvasContainerRef,
      this.context.modelingGraph,
      paperOptions,
      this.props.grid,
      this.props.gridSize,
      this.props.gridBackground,
    )
      .historyClb(this.historyClb)
      .interactionClb(this.interactionClb)
      .nodeClb(this.nodeClb)
      .event(PaperEventTypes.SELECT)
      .event(PaperEventTypes.NODE_SELECT)
      .event(PaperEventTypes.LINK_SELECT)
      .event(PaperEventTypes.PAPER_DRAG)
      .event(PaperEventTypes.PAPER_ZOOM)
      .event(PaperEventTypes.LINK_ELEMENT_TOOLS)
      .event(PaperEventTypes.NODE_ELEMENT_TOOLS)
      .event(PaperEventTypes.NODE_CONTEXT_MENU)
      .event(PaperEventTypes.LINK_DRAG)
      .event(PaperEventTypes.NODE_EMBED)
      .event(PaperEventTypes.NODE_DELETE)
      .event(PaperEventTypes.MESSAGE_EVENT_NODE_COPY_PASTE)
      .event(PaperEventTypes.KEYBOARD_EVENT_NODE_COPY_PASTE)
      .build();
  };

  nodeClb = (node: joint.dia.Cell) => {
    this.props.setNode(node);
  };

  interactionClb = (
    interactionType: InteractionType,
    interactionTime: number,
    payload?: KEAGraph | joint.dia.Link | joint.dia.Element | joint.dia.Cell[],
  ) => {
    this.props.addUserInteraction(interactionType, interactionTime, payload);
  };

  historyClb = () => {
    this.props.pushPresent();
  };

  render() {
    return (
      <div
        style={{ width: this.props.width, height: this.props.height, position: "relative" }}
        className="box m-0 p-0"
      >
        <div id="canvas" ref={this.canvasContainerRef}></div>
        {this.state.contextMenu && (
          <ElementContextMenu
            target={this.state.contextMenu}
            onClose={() => this.setState({ contextMenu: null })}
            onRename={() => this.state.contextMenu?.paper.trigger("keaelement:rename", this.state.contextMenu.view)}
            onProperties={() => this.props.setNode(this.state.contextMenu?.view.model)}
            pushPresent={this.props.pushPresent}
            addUserInteraction={this.props.addUserInteraction}
          />
        )}
        {this.state.append && !this.state.renaming && (
          <AppendPalette
            target={this.state.append}
            pushPresent={this.props.pushPresent}
            addUserInteraction={this.props.addUserInteraction}
          />
        )}
        {this.state.renaming && (
          <InlineLabelEditor
            target={this.state.renaming}
            onClose={() => this.setState({ renaming: null })}
            pushPresent={this.props.pushPresent}
            addUserInteraction={this.props.addUserInteraction}
          />
        )}
      </div>
    );
  }
}
